import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Plus, Trash2, Loader2, Search, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { APIProvider, Map, AdvancedMarker, useMap } from "@vis.gl/react-google-maps";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || "";

// Default center (Phoenix, AZ)
const DEFAULT_CENTER = { lat: 33.4484, lng: -112.0740 };
const DEFAULT_ZOOM = 10;

// Circle overlay component that draws service area circles on the map
const ServiceAreaCircle = ({ area, color = "#1a73e8" }) => {
  const map = useMap();
  
  useEffect(() => {
    if (!map || !area.lat || !area.lng || !window.google) return;
    
    // Convert miles to meters (1 mile = 1609.34 meters)
    const radiusMeters = (area.radius_miles || 25) * 1609.34;
    
    const circle = new window.google.maps.Circle({
      center: { lat: area.lat, lng: area.lng },
      radius: radiusMeters,
      fillColor: color,
      fillOpacity: 0.15,
      strokeColor: color,
      strokeOpacity: 0.8,
      strokeWeight: 2,
      map: map,
    });
    
    return () => {
      circle.setMap(null);
    };
  }, [map, area, color]);
  
  return null;
};

// Map content component that has access to the map instance
const MapContent = ({ areasGeo, center }) => {
  const map = useMap();
  
  // Fit bounds to show all areas
  useEffect(() => {
    if (!map || !window.google || areasGeo.length === 0) return;
    
    const bounds = new window.google.maps.LatLngBounds();
    areasGeo.forEach(area => {
      if (area.lat && area.lng) {
        bounds.extend({ lat: area.lat, lng: area.lng });
      }
    });
    
    if (areasGeo.length > 0) {
      map.fitBounds(bounds, { padding: 50 });
    }
  }, [map, areasGeo]);
  
  return (
    <>
      {areasGeo.map((area, idx) => (
        <div key={idx}>
          <ServiceAreaCircle area={area} color="#1a73e8" />
          <AdvancedMarker
            position={{ lat: area.lat, lng: area.lng }}
            title={area.name}
          >
            <div className="w-8 h-8 bg-[#1a73e8] rounded-full flex items-center justify-center shadow-lg border-2 border-white">
              <MapPin className="w-4 h-4 text-white" />
            </div>
          </AdvancedMarker>
        </div>
      ))}
    </>
  );
};

const ServiceAreaMap = ({ workerId, editable = false }) => {
  const [areas, setAreas] = useState([]);
  const [areasGeo, setAreasGeo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [newArea, setNewArea] = useState({
    name: "",
    lat: null,
    lng: null,
    radius_miles: 25
  });
  
  const hasValidApiKey = GOOGLE_MAPS_KEY && GOOGLE_MAPS_KEY !== "" && GOOGLE_MAPS_KEY !== "YOUR_GOOGLE_MAPS_API_KEY";

  useEffect(() => {
    fetchServiceAreas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workerId]);

  const fetchServiceAreas = async () => {
    try {
      const response = await axios.get(`${API}/service-areas/${workerId}`);
      setAreas(response.data.areas || []);
      setAreasGeo(response.data.areas_geo || []);
    } catch (error) {
      console.error("Error fetching service areas:", error);
    } finally {
      setLoading(false);
    }
  };

  const searchLocation = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      // Use Google Maps Geocoding if available
      if (hasValidApiKey && window.google) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: searchQuery }, (results, status) => {
          if (status === "OK" && results.length > 0) {
            setSearchResults(results.slice(0, 5).map(r => ({
              name: r.formatted_address,
              lat: r.geometry.location.lat(),
              lng: r.geometry.location.lng()
            })));
          } else {
            // Fallback mock results
            setSearchResults([{
              name: searchQuery,
              lat: DEFAULT_CENTER.lat + (Math.random() - 0.5) * 0.2,
              lng: DEFAULT_CENTER.lng + (Math.random() - 0.5) * 0.2
            }]);
          }
          setSearching(false);
        });
      } else {
        // Mock results when no API key
        const mockResults = [{
          name: searchQuery,
          lat: DEFAULT_CENTER.lat + (Math.random() - 0.5) * 0.2,
          lng: DEFAULT_CENTER.lng + (Math.random() - 0.5) * 0.2
        }];
        setSearchResults(mockResults);
        setSearching(false);
      }
    } catch (error) {
      toast.error("Error searching location");
      setSearching(false);
    }
  };

  const selectLocation = (result) => {
    setNewArea({
      ...newArea,
      name: result.name,
      lat: result.lat,
      lng: result.lng
    });
    setSearchResults([]);
    setSearchQuery("");
  };

  const addArea = async () => {
    if (!newArea.name || !newArea.lat || !newArea.lng) {
      toast.error("Please select a location");
      return;
    }

    setSaving(true);
    try {
      const updatedAreas = [...areasGeo, newArea];
      await axios.post(`${API}/service-areas`, {
        worker_id: workerId,
        areas: updatedAreas
      });
      
      toast.success("Service area added!");
      setAreasGeo(updatedAreas);
      setShowModal(false);
      setNewArea({ name: "", lat: null, lng: null, radius_miles: 25 });
    } catch (error) {
      toast.error("Failed to add service area");
    } finally {
      setSaving(false);
    }
  };

  const removeArea = async (index) => {
    setSaving(true);
    try {
      const updatedAreas = areasGeo.filter((_, i) => i !== index);
      await axios.post(`${API}/service-areas`, {
        worker_id: workerId,
        areas: updatedAreas
      });
      
      toast.success("Service area removed!");
      setAreasGeo(updatedAreas);
    } catch (error) {
      toast.error("Failed to remove service area");
    } finally {
      setSaving(false);
    }
  };

  // Calculate map center based on areas
  const getMapCenter = useCallback(() => {
    if (areasGeo.length === 0) return DEFAULT_CENTER;
    const avgLat = areasGeo.reduce((sum, a) => sum + (a.lat || 0), 0) / areasGeo.length;
    const avgLng = areasGeo.reduce((sum, a) => sum + (a.lng || 0), 0) / areasGeo.length;
    return { lat: avgLat, lng: avgLng };
  }, [areasGeo]);

  // Render map without API key (fallback visualization)
  const renderFallbackMap = () => (
    <div className="relative bg-gradient-to-br from-[#e8f0fe] to-[#f8f9fa] rounded-xl h-64 overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
          {[...Array(10)].map((_, i) => (
            <line key={`h${i}`} x1="0" y1={i * 10} x2="100" y2={i * 10} stroke="#1a73e8" strokeWidth="0.5" />
          ))}
          {[...Array(10)].map((_, i) => (
            <line key={`v${i}`} x1={i * 10} y1="0" x2={i * 10} y2="100" stroke="#1a73e8" strokeWidth="0.5" />
          ))}
        </svg>
      </div>
      
      {areasGeo.length > 0 ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-3">
              {areasGeo.slice(0, 3).map((_, idx) => (
                <div key={idx} className="w-10 h-10 rounded-full bg-[#1a73e8]/20 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-[#1a73e8]" />
                </div>
              ))}
            </div>
            <p className="text-sm font-medium text-[#202124]">
              {areasGeo.length} service area{areasGeo.length > 1 ? "s" : ""} configured
            </p>
            {!hasValidApiKey && (
              <p className="text-xs text-[#5f6368] mt-2 flex items-center justify-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Add Google Maps API key for interactive map
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <MapPin className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-[#5f6368]">No service areas defined</p>
            {editable && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowModal(true)}
                className="mt-2 rounded-full"
              >
                Add your first area
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="service-area-map">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-[#202124] flex items-center gap-2">
          <MapPin className="w-5 h-5 text-[#1a73e8]" />
          Service Areas
        </h3>
        {editable && (
          <Button
            size="sm"
            onClick={() => setShowModal(true)}
            className="bg-[#1a73e8] hover:bg-[#1557b0] rounded-full"
            data-testid="add-area-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Area
          </Button>
        )}
      </div>

      {/* Map Display */}
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-[#1a73e8]" />
        </div>
      ) : (
        <>
          {/* Google Maps or Fallback */}
          {hasValidApiKey ? (
            <APIProvider apiKey={GOOGLE_MAPS_KEY}>
              <div className="rounded-xl overflow-hidden h-64" data-testid="google-map-container">
                <Map
                  style={{ width: "100%", height: "100%" }}
                  defaultCenter={getMapCenter()}
                  defaultZoom={areasGeo.length > 0 ? 9 : DEFAULT_ZOOM}
                  mapId="service-area-map"
                  gestureHandling="cooperative"
                  disableDefaultUI={true}
                  zoomControl={true}
                  mapTypeControl={false}
                  streetViewControl={false}
                  fullscreenControl={false}
                >
                  <MapContent areasGeo={areasGeo} center={getMapCenter()} />
                </Map>
              </div>
            </APIProvider>
          ) : (
            renderFallbackMap()
          )}

          {/* Areas List */}
          {areasGeo.length > 0 && (
            <div className="space-y-2">
              {areasGeo.map((area, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center justify-between p-3 bg-[#f8f9fa] rounded-lg"
                  data-testid={`service-area-${idx}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#e8f0fe] flex items-center justify-center">
                      <MapPin className="w-4 h-4 text-[#1a73e8]" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[#202124]">{area.name}</p>
                      <p className="text-xs text-[#5f6368]">{area.radius_miles} mile radius</p>
                    </div>
                  </div>
                  {editable && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeArea(idx)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid={`remove-area-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Legacy text areas (if no geo areas) */}
          {areasGeo.length === 0 && areas.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {areas.map((area, idx) => (
                <span 
                  key={idx} 
                  className="px-3 py-1 bg-[#f8f9fa] text-[#5f6368] rounded-full text-sm"
                >
                  {area}
                </span>
              ))}
            </div>
          )}
        </>
      )}

      {/* Add Area Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#1a73e8]" />
              Add Service Area
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Search Location</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Enter city, zip code, or address..."
                  onKeyDown={(e) => e.key === "Enter" && searchLocation()}
                  data-testid="location-search-input"
                />
                <Button
                  type="button"
                  onClick={searchLocation}
                  disabled={searching}
                  className="bg-[#1a73e8] hover:bg-[#1557b0]"
                >
                  {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="max-h-40 overflow-y-auto border rounded-lg">
                {searchResults.map((result, idx) => (
                  <button
                    key={idx}
                    onClick={() => selectLocation(result)}
                    className="w-full text-left px-3 py-2 hover:bg-[#f8f9fa] border-b last:border-b-0 flex items-center gap-2"
                  >
                    <MapPin className="w-4 h-4 text-[#5f6368] shrink-0" />
                    <span className="text-sm text-[#202124] truncate">{result.name}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Selected Location */}
            {newArea.name && (
              <div className="p-3 bg-[#e6f4ea] rounded-lg">
                <p className="text-sm font-medium text-[#1e8e3e]">Selected:</p>
                <p className="text-sm text-[#202124]">{newArea.name}</p>
              </div>
            )}

            <div>
              <Label>Service Radius (miles)</Label>
              <Input
                type="number"
                min="1"
                max="100"
                value={newArea.radius_miles}
                onChange={(e) => setNewArea({...newArea, radius_miles: parseInt(e.target.value) || 25})}
                className="mt-1"
                data-testid="radius-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={addArea}
              disabled={saving || !newArea.name}
              className="bg-[#1a73e8] hover:bg-[#1557b0]"
              data-testid="confirm-add-area-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Area"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiceAreaMap;
